import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import type { BidType, ElectricityPriceScenario, ElplanInfo, PlanValues } from 'api/elplan/elplan.types'
import { queryClient } from 'helpers/queryClient'
import Datetime from 'utils/datetime/datetime'
import { proxy } from 'valtio'
import { subscribeKey } from 'valtio/utils'

export type ElplanDateView = 'tomorrow' | 'today' | 'optimizationPeriod' | 'custom' | ''

type ElplanState = {
  elplan_id: number
  is_locked: boolean
  has_plan: boolean
  values: PlanValues[]
  date: ISODateTime | string
  day_ahead_supplier_results?: PlanValues[]
}

type ElplanStore = Omit<ElplanInfo, 'date' | 'partitions'> & {
  date: {
    start_time?: ISODateTime
    end_time?: ISODateTime
    current_view?: ElplanDateView
  }
  elplan: ElplanState
  elplans_by_date: Record<string, ElplanState>
  hasPassedDeadline: boolean
  spotBidWithoutPriceInfo: { amount: number | undefined; minimum_hours_for_bid: number | undefined }
  blockBidInfo: { amount: number | undefined; minimum_hours_for_bid: number | undefined }
  brokerDeadlineTime: `${number}:${number}:${number}`
  prettyBrokerDeadlineTime: string
  only_positive_bids?: boolean
  profileBidInfo: {
    amount?: number
    minimum_hours_for_bid?: number
  }
}

function createEmptyElplan(date: ISODateTime | string = ''): ElplanState {
  return {
    elplan_id: 0,
    is_locked: false,
    has_plan: false,
    values: [],
    date,
    day_ahead_supplier_results: [],
  }
}

function toDateKey(date?: ISODateTime): string {
  if (!date) return ''
  return (Datetime.toLocalTime(date, 'onlyDate') as ISODateTime) || ''
}

const elplanStore = proxy<ElplanStore>({
  id: 0,
  elplan_info_id: 0,
  broker_name: '',
  excel_file_name: '',
  currency: '',
  bid_types: [],
  excel_export_footer_text: '',
  integration: '',
  maxBidVolume: null,
  max_amount_block_and_profile_bids: undefined,
  only_positive_bids: false,
  date: {
    start_time: undefined,
    end_time: undefined,
    current_view: undefined,
  },
  elplans_by_date: {},
  scenarios: [],
  deadline_time: undefined,
  get elplan(): ElplanState {
    const dateKey = toDateKey(this.date.start_time)
    if (!dateKey) {
      return createEmptyElplan()
    }
    return this.elplans_by_date[dateKey] || createEmptyElplan(dateKey)
  },
  get hasPassedDeadline() {
    if (this.deadline_time) {
      const now = Datetime.getISONow()
      const deadlineTime = new Date()
      const [hours, minutes, seconds] = this.deadline_time.split(':').map(Number)
      deadlineTime.setHours(hours, minutes, seconds, 0)
      if (Datetime.isAfter(now, deadlineTime.toISOString() as ISODateTime)) {
        return true
      }
    }

    return false
  },
  get brokerDeadlineTime() {
    return this.deadline_time ? this.deadline_time : '11:30:00'
  },

  get prettyBrokerDeadlineTime() {
    if (this.deadline_time) {
      const deadlineTime = new Date()
      const [hours, minutes, seconds] = this.deadline_time.split(':').map(Number)
      deadlineTime.setHours(hours, minutes, seconds, 0)
      return Datetime.toLocalTime(deadlineTime.toISOString() as ISODateTime, 'hour')
    }
    return '11:30'
  },
  get spotBidWithoutPriceInfo() {
    const spotBidInfo = this.bid_types.find((type: BidType) => type.type === 'spotbid_without_price')

    return {
      amount: spotBidInfo?.amount,
      minimum_hours_for_bid: spotBidInfo?.minimum_hours_for_bid,
    }
  },
  get blockBidInfo() {
    const blockBidInfo = this.bid_types.find((type: BidType) => type.type === 'blockbid')

    return {
      amount: blockBidInfo?.amount,
      minimum_hours_for_bid: blockBidInfo?.minimum_hours_for_bid,
    }
  },
  get profileBidInfo() {
    const profileBidInfo = this.bid_types.find((type: BidType) => type.type === 'profilebid')

    return {
      amount: profileBidInfo?.amount,
      minimum_hours_for_bid: profileBidInfo?.minimum_hours_for_bid,
    }
  },
})

function deriveCurrentDateView(startTime?: ISODateTime, endTime?: ISODateTime): ElplanDateView {
  if (!startTime || !endTime) {
    return 'tomorrow'
  }

  const tomorrow = Datetime.getTomorrowDate()
  if (startTime === tomorrow.startTime && endTime === tomorrow.endTime) {
    return 'tomorrow'
  }

  const today = Datetime.getTodayDate()
  if (startTime === today.startTime && endTime === today.endTime) {
    return 'today'
  }

  if (startTime === today.startTime && Datetime.isAfter(endTime, today.endTime)) {
    return 'optimizationPeriod'
  }

  return 'custom'
}

subscribeKey(elplanStore, `id`, (elplanInfoId) => {
  elplanStore.id = elplanInfoId ?? 0
  queryClient.invalidateQueries({ queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY] })
})

export function setElplanInfoStore(
  deadline_time: `${number}:${number}:${number}` | undefined,
  broker_name: string,
  currency: string,
  // Optional because the API response is cast, not parsed — the field can be absent at runtime.
  bid_types: BidType[] | undefined,
  excel_file_name: string,
  id: number,
  excelExportFooterText?: string,
  integration?: string | undefined | null,
  maxBidVolume?: number | undefined | null,
  maxAmountBlockAndProfileBids?: number,
  only_positive_bids?: boolean,
  scenarios?: ElectricityPriceScenario[]
): void {
  // Clear per-date cache when switching elplan context to avoid stale cross-context plan reuse.
  if (elplanStore.elplan_info_id !== id) {
    elplanStore.elplans_by_date = {}
  }

  elplanStore.deadline_time = deadline_time
  elplanStore.broker_name = broker_name
  elplanStore.currency = currency
  elplanStore.bid_types = bid_types ?? []
  elplanStore.excel_file_name = excel_file_name
  elplanStore.elplan_info_id = id
  elplanStore.excel_export_footer_text = excelExportFooterText
  elplanStore.integration = integration
  elplanStore.maxBidVolume = maxBidVolume
  elplanStore.max_amount_block_and_profile_bids = maxAmountBlockAndProfileBids
  elplanStore.only_positive_bids = only_positive_bids
  elplanStore.scenarios = scenarios ?? []
}

export function setCurrentDate(startTime: ISODateTime, endTime: ISODateTime): void {
  elplanStore.date.start_time = startTime
  elplanStore.date.end_time = endTime
  elplanStore.date.current_view = deriveCurrentDateView(startTime, endTime)
}

export function setCurrentDateView(currentView: ElplanDateView): void {
  elplanStore.date.current_view = currentView
}

export function setElplan(
  id: number,
  isLocked: boolean,
  hasPlan: boolean,
  values: PlanValues[],
  date: ISODateTime,
  day_ahead_supplier_results?: PlanValues[]
): void {
  const dateKey = Datetime.toLocalTime(date, 'onlyDate') as ISODateTime

  if (!dateKey) {
    return
  }

  const nextElplan = createEmptyElplan(dateKey)
  nextElplan.elplan_id = id
  nextElplan.is_locked = isLocked
  nextElplan.has_plan = hasPlan
  // Deep clone values to prevent external mutations from corrupting store state
  nextElplan.values = values.map((value) => ({ ...value }))
  nextElplan.day_ahead_supplier_results = (day_ahead_supplier_results ?? []).map((value) => ({ ...value }))

  elplanStore.elplans_by_date[dateKey] = nextElplan
}

export default elplanStore
